import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { usePostHog } from 'posthog-js/react'
import { getCurrentUser as getCognitoCurrentUser } from 'aws-amplify/auth'
import posthog from 'posthog-js'
import { InterviewStatus } from '../../api/gpt/types'
import { AiInterviewMetrics } from '../../ui/components/ai-interviewer/AudioQuestionBox'

// Define event names as constants to prevent typos and enable autocomplete
export const ANALYTICS_EVENTS = {
  PAGE_VIEW: '$pageview',
  ADVISOR_ASKED: 'advisor_asked',
  VIDEO_ACTION: 'video_action',
  JOB_OFFER_LINK_CLICKED: 'job_offer_link_clicked',
  LINKEDIN_VERIFICATION_STARTED: 'linkedin_verification_started',
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  INTERVIEW_STARTED: 'interview_started',
  INTERVIEW_STATUS_UPDATED: 'interview_status_updated',
  INTERVIEW_COMPLETED: 'interview_completed',
  SIGNUP_EMAIL_VERIFIED: 'signup_email_verified',
  AI_FEEDBACK: 'ai_feedback',
  AI_INTERVIEW_NEW_QUESTION: 'ai_interview_new_question',
  REACHED_FREE_USAGES_LIMIT: 'reached_free_usages_limit',
} as const

// Type for all possible event names
type AnalyticsEvent = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS]

// Type for event properties
type EventProperties = {
  [ANALYTICS_EVENTS.ADVISOR_ASKED]: { question: string }
  [ANALYTICS_EVENTS.VIDEO_ACTION]: { action: string }
  [ANALYTICS_EVENTS.JOB_OFFER_LINK_CLICKED]: { url: string }
  [ANALYTICS_EVENTS.PAGE_VIEW]: Record<string, never>
  [ANALYTICS_EVENTS.LINKEDIN_VERIFICATION_STARTED]: { user_type: string }
  [ANALYTICS_EVENTS.ONBOARDING_STARTED]: Record<string, never>
  [ANALYTICS_EVENTS.ONBOARDING_COMPLETED]: Record<string, never>
  [ANALYTICS_EVENTS.INTERVIEW_STATUS_UPDATED]: InterviewStatus
  [ANALYTICS_EVENTS.INTERVIEW_STARTED]: {session_id: string, topic: string, total_main_questions: number, max_follow_ups_per_main: number}
  [ANALYTICS_EVENTS.INTERVIEW_COMPLETED]: {session_id: string, decision: boolean, feedback: string}
  [ANALYTICS_EVENTS.SIGNUP_EMAIL_VERIFIED]: Record<string, never>
  [ANALYTICS_EVENTS.AI_FEEDBACK]: { sentiment: boolean, content: string }
  [ANALYTICS_EVENTS.AI_INTERVIEW_NEW_QUESTION]: AiInterviewMetrics
  [ANALYTICS_EVENTS.REACHED_FREE_USAGES_LIMIT]: undefined
}

export const useAnalytics = () => {
  const posthog = usePostHog()

  const trackEvent = <T extends AnalyticsEvent>(
    eventName: T,
    properties?: EventProperties[T]
  ) => {
    posthog?.capture(eventName, properties)
  }

  return { trackEvent }
}

const PostHogPageviewTracker = () => {
  const location = useLocation()
  const { trackEvent } = useAnalytics()


  useEffect(() => {
    const identifyUser = async () => {
      try {
        const cognitoUser = await getCognitoCurrentUser()
        posthog.identify(cognitoUser?.userId?.toString(), { email: cognitoUser?.signInDetails?.loginId })
      } catch (error) {
        console.warn('Could not identify user with posthog, maybe user is not yet signed in', error)
      }
    }
    if(!posthog._isIdentified()) {
      identifyUser()
    }
  }, [])
  useEffect(() => {
    if(posthog._isIdentified()) {
      trackEvent(ANALYTICS_EVENTS.PAGE_VIEW)
    }
  }, [location])


  return null
}

export const reportErrorToPostHog = (error: Error) => {
  posthog?.captureException(error)
}

// Global reference for non-React code
let globalTrackEvent: ((event: AnalyticsEvent) => void) | null = null
export const setGlobalTrackEvent = (trackEvent: (event: AnalyticsEvent) => void) => {
  globalTrackEvent = trackEvent
}
export const getGlobalTrackEvent = () => globalTrackEvent

export default PostHogPageviewTracker


